/**
 * AcronIQ Veritus Report Builder - Plain Text Export Utility
 * Converts structured report data to formatted plain text
 */

/**
 * Convert a structured report to plain text format
 * @param {Object} report - The structured report object
 * @returns {string} Formatted plain text report
 */
export function reportToText(report) {
  if (!report || !report.sections) {
    return 'No report data available';
  }

  const { sections, meta } = report;
  const lines = [];

  // Header
  lines.push('='.repeat(60));
  lines.push('ACRONIQ VERITUS RESEARCH REPORT');
  lines.push('='.repeat(60));
  lines.push('');

  if (meta?.generatedAt) {
    const date = new Date(meta.generatedAt).toLocaleString('en-GB');
    lines.push(`Generated: ${date}`);
    lines.push('');
  }

  // Executive Summary
  if (sections.executiveSummary?.length > 0) {
    lines.push('EXECUTIVE SUMMARY');
    lines.push('-'.repeat(20));
    sections.executiveSummary.forEach(item => {
      lines.push(`• ${item}`);
    });
    lines.push('');
  }

  // Context & Objective
  if (sections.context?.length > 0) {
    lines.push('CONTEXT & OBJECTIVE');
    lines.push('-'.repeat(20));
    sections.context.forEach(item => {
      lines.push(`• ${item}`);
    });
    lines.push('');
  }

  // Research Insights
  if (sections.researchInsights?.length > 0) {
    lines.push('RESEARCH INSIGHTS');
    lines.push('-'.repeat(20));
    sections.researchInsights.forEach(item => {
      lines.push(`• ${item}`);
    });
    lines.push('');
  }

  // Analysis & Interpretation
  if (sections.analysis?.length > 0) {
    lines.push('ANALYSIS & INTERPRETATION');
    lines.push('-'.repeat(25));
    sections.analysis.forEach(item => {
      lines.push(`• ${item}`);
    });
    lines.push('');
  }

  // Recommendations
  if (sections.recommendations?.length > 0) {
    lines.push('RECOMMENDATIONS');
    lines.push('-'.repeat(15));
    sections.recommendations.forEach(item => {
      lines.push(`${item}`);
    });
    lines.push('');
  }

  // Implementation Roadmap
  if (sections.roadmap) {
    lines.push('IMPLEMENTATION ROADMAP');
    lines.push('-'.repeat(22));
    
    if (sections.roadmap.now?.length > 0) {
      lines.push('NOW (Immediate):');
      sections.roadmap.now.forEach(item => {
        lines.push(`  • ${item}`);
      });
      lines.push('');
    }

    if (sections.roadmap.next?.length > 0) {
      lines.push('NEXT (Short-term):');
      sections.roadmap.next.forEach(item => {
        lines.push(`  • ${item}`);
      });
      lines.push('');
    }

    if (sections.roadmap.later?.length > 0) {
      lines.push('LATER (Long-term):');
      sections.roadmap.later.forEach(item => {
        lines.push(`  • ${item}`);
      });
      lines.push('');
    }
  }

  // Appendix/Sources
  if (sections.appendix?.length > 0) {
    lines.push('APPENDIX/SOURCES');
    lines.push('-'.repeat(16));
    sections.appendix.forEach(item => {
      lines.push(`• ${item}`);
    });
    lines.push('');
  }

  // Footer
  if (meta?.totalItems !== undefined) {
    lines.push('-'.repeat(60));
    lines.push(`Total items analysed: ${meta.totalItems}`);
    
    if (meta.itemCounts) {
      lines.push('');
      lines.push('Item breakdown:');
      Object.entries(meta.itemCounts).forEach(([tag, count]) => {
        if (count > 0) {
          lines.push(`  ${tag}: ${count}`);
        }
      });
    }
  }

  lines.push('');
  lines.push('Generated by AcronIQ Veritus Report Builder v1');
  lines.push('='.repeat(60));

  return lines.join('\n');
}

/**
 * Copy text to clipboard with fallback for older browsers
 * @param {string} text - Text to copy to clipboard
 * @returns {Promise<boolean>} Success status
 */
export async function copyToClipboard(text) {
  try {
    // Modern clipboard API
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    const success = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    return success;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
}
